import { useState } from "react";
import { useCinematic } from "@/data/cinematicMode/context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Save,
  RotateCcw,
  Film,
  MapPin,
  MessageSquare,
  Eye,
  Lightbulb,
  DollarSign,
  MousePointerClick,
  CheckCircle2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { Scene } from "@/data/cinematicMode/types";

export function SceneEditorPanel() {
  const { scenes, updateScene, resetScenes } = useCinematic();
  const [editedIndices, setEditedIndices] = useState<Set<number>>(new Set());
  const [savedIndices, setSavedIndices] = useState<Set<number>>(new Set());

  const handleFieldChange = (index: number, field: keyof Scene, value: string) => {
    updateScene(index, { [field]: value });
    setEditedIndices((prev) => new Set(prev).add(index));
    setSavedIndices((prev) => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  };

  const handleSave = (index: number) => {
    setEditedIndices((prev) => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
    setSavedIndices((prev) => new Set(prev).add(index));
    setTimeout(() => {
      setSavedIndices((prev) => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    }, 2000);
  };

  const handleResetAll = () => {
    resetScenes();
    setEditedIndices(new Set());
    setSavedIndices(new Set());
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Film className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold text-foreground">Scene Editor</h2>
          <Badge variant="outline" className="text-[10px] font-mono">
            {scenes.length} scenes
          </Badge>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handleResetAll}>
          <RotateCcw className="h-3 w-3" />
          Reset All to Defaults
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Edit scene titles, narration, routes, and interaction hints. Changes apply immediately to the Cinematic Demo.
      </p>

      {/* Scene List */}
      <Accordion type="single" collapsible className="space-y-2">
        {scenes.map((scene, index) => {
          const isEdited = editedIndices.has(index);
          const isSaved = savedIndices.has(index);

          return (
            <AccordionItem
              key={scene.id}
              value={scene.id}
              className="border rounded-lg overflow-hidden"
            >
              <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-accent/30">
                <div className="flex items-center gap-3 w-full">
                  <Badge
                    variant="outline"
                    className="text-[10px] font-mono h-5 w-5 flex items-center justify-center p-0 shrink-0"
                  >
                    {index + 1}
                  </Badge>
                  <div className="flex-1 text-left min-w-0">
                    <span className="text-sm font-medium text-foreground">{scene.title}</span>
                    <span className="text-xs text-muted-foreground ml-2 hidden sm:inline">
                      {scene.route}
                    </span>
                  </div>
                  {isSaved && (
                    <Badge className="bg-success/10 text-success border-success/20 text-[10px] gap-1">
                      <CheckCircle2 className="h-2.5 w-2.5" /> Saved
                    </Badge>
                  )}
                  {isEdited && (
                    <Badge variant="secondary" className="text-[10px]">
                      Unsaved
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4 pt-2">
                  {/* Row 1: Title + Subtitle + Route */}
                  <div className="grid grid-cols-3 gap-3">
                    <FieldGroup label="Title" icon={Film}>
                      <Input
                        value={scene.title}
                        onChange={(e) => handleFieldChange(index, "title", e.target.value)}
                        className="h-8 text-xs"
                      />
                    </FieldGroup>
                    <FieldGroup label="Subtitle" icon={MessageSquare}>
                      <Input
                        value={scene.subtitle}
                        onChange={(e) => handleFieldChange(index, "subtitle", e.target.value)}
                        className="h-8 text-xs"
                      />
                    </FieldGroup>
                    <FieldGroup label="Route" icon={MapPin}>
                      <Input
                        value={scene.route}
                        onChange={(e) => handleFieldChange(index, "route", e.target.value)}
                        className="h-8 text-xs font-mono"
                      />
                    </FieldGroup>
                  </div>

                  {/* Row 2: Narration */}
                  <FieldGroup label="Narration" icon={MessageSquare}>
                    <Textarea
                      value={scene.narration}
                      onChange={(e) => handleFieldChange(index, "narration", e.target.value)}
                      className="text-xs min-h-[56px] resize-none"
                      rows={2}
                    />
                  </FieldGroup>

                  {/* Row 3: Insight cards */}
                  <div className="grid grid-cols-2 gap-3">
                    <FieldGroup label="What's Happening" icon={Eye} color="text-info">
                      <Textarea
                        value={scene.whatHappens}
                        onChange={(e) => handleFieldChange(index, "whatHappens", e.target.value)}
                        className="text-xs min-h-[56px] resize-none"
                        rows={2}
                      />
                    </FieldGroup>
                    <FieldGroup label="Why It Matters" icon={Lightbulb} color="text-warning">
                      <Textarea
                        value={scene.whyItMatters}
                        onChange={(e) => handleFieldChange(index, "whyItMatters", e.target.value)}
                        className="text-xs min-h-[56px] resize-none"
                        rows={2}
                      />
                    </FieldGroup>
                    <FieldGroup label="Business Impact" icon={DollarSign} color="text-success">
                      <Textarea
                        value={scene.businessImpact}
                        onChange={(e) => handleFieldChange(index, "businessImpact", e.target.value)}
                        className="text-xs min-h-[56px] resize-none"
                        rows={2}
                      />
                    </FieldGroup>
                    <FieldGroup label="Interaction Hint" icon={MousePointerClick} color="text-primary">
                      <Textarea
                        value={scene.interactionHint}
                        onChange={(e) => handleFieldChange(index, "interactionHint", e.target.value)}
                        className="text-xs min-h-[56px] resize-none"
                        rows={2}
                      />
                    </FieldGroup>
                  </div>

                  {/* Save button */}
                  <div className="flex justify-end pt-1">
                    <Button
                      size="sm"
                      className="gap-1.5 text-xs h-7"
                      disabled={!isEdited}
                      onClick={() => handleSave(index)}
                    >
                      <Save className="h-3 w-3" />
                      Apply Changes
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}

function FieldGroup({
  label,
  icon: Icon,
  color,
  children,
}: {
  label: string;
  icon: React.ElementType;
  color?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <Icon className={cn("h-3 w-3 shrink-0", color || "text-muted-foreground")} />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
      </div>
      {children}
    </div>
  );
}
